import { TradersYardApi } from '../models/api';
import { ChatConversationMessage, ChatMessageID, Meeting, MessageListResponse } from '../models/chat';
import { CacheService } from './cache.service';
import { ChatMessageSenderService } from './chat-message-sender.service';
import { chatStore } from './chat.store';
import { ConversationsStore } from './conversations.store';
import { MeetingsStore } from './meetings.store';
import { AuthServiceInterface } from './AuthServiceInterface';

export type MessageStreamCache = { [id: number]: Rx.Observable<GroupedConversation[]> };
export type GroupedConversation = { root: ChatConversationMessage; children: ChatConversationMessage[] };

export enum ChatViewMode {
    /** shows a loading view while we fetch messages */
    LOADING = 'LOADING',
    /** regular message list view */
    MESSAGES = 'MESSAGES',
    /** views should show message search results */
    SEARCH_RESULT = 'SEARCH_RESULT',
}

export const PUBLIC_CHAT_ID = 0;

export class ChatConversationService {
    static $inject = [
        'ConversationsStore',
        'MeetingsStore',
        'ChatMessageSenderService',
        'CacheService',
        '$http',
        'AuthService',
    ];
    public quotedMessage$ = new Rx.Subject<ChatConversationMessage>();
    public editMessage$ = new Rx.Subject<ChatConversationMessage>();
    public gotoMessage$ = new Rx.Subject<undefined | ChatConversationMessage>();
    public searchResult$ = new Rx.BehaviorSubject<ChatConversationMessage[]>([]);

    /**
     * We can search through messages using a search term. This property holds the searched term.
     * You can set it using {@link dispatchSearchMessages}.
     *
     * As son as searchTerm is set via {@link dispatchSearchMessages}, the {@link viewMode$} will be changed.
     */
    public searchTerm$ = new Rx.BehaviorSubject<string | undefined>(undefined);

    /**
     * The chat can switch between different views. E.g. regular reading mode or search results view.
     * Since we don't have a router integrated, we have to show/hide elements AngularJS directives like `ng-if`.
     *
     * REFACTOR: Ideally we should use router to change views.
     */
    public viewMode$ = new Rx.BehaviorSubject<ChatViewMode>(ChatViewMode.MESSAGES);

    /**
     * Emits when someone requested to exit search mode with {@link dispatchExitSearch}
     */
    public onSearchExit$ = new Rx.Subject();
    protected cache: MessageStreamCache = {};

    constructor(
        private conversationsStore: ConversationsStore,
        private meetingsStore: MeetingsStore,
        private chatMessageSender: ChatMessageSenderService,
        private cacheService: CacheService,
        private $http: angular.IHttpService,
        private authService: AuthServiceInterface
    ) {}

    public getConversation$(meetingId: number) {
        return this.conversationsStore.getConversation$(meetingId);
    }

    public editLastMessage(meeting: Meeting) {
        this.conversationsStore
            .getConversationMessages$(meeting.id)
            .take(1)
            .subscribe((messages) => {
                const userId = this.authService.getUserId();

                const userMessages = messages.filter((message) => message.owner.id === userId);

                const lastMessage = userMessages[userMessages.length - 1];

                if (lastMessage) {
                    this.editMessage$.onNext(lastMessage);
                }
            });
    }

    public async dispatchFetchMessages(meetingId: number, mode: 'replace' | 'merge' = 'merge') {
        const conversationStore = chatStore.getOrCreateConversation(meetingId);
        // fetch and toggle loading state of conversation view
        conversationStore.setIsLoading(true);
        const { messages, more_pages_exist } = await this.fetchMessages(meetingId);
        conversationStore.setIsLoading(false);

        switch (mode) {
            case 'merge':
                return this.conversationsStore.dispatchMergeMessages(meetingId, messages, more_pages_exist);
            case 'replace':
                return this.conversationsStore.dispatchSetMessages(meetingId, messages, {
                    hasPreviousMessages: more_pages_exist,
                });
        }
    }

    /**
     * Set search term that will be used to filter messages in the conversation view.
     * As soon as you set the search term, the {@link viewMode$} will be changed to {@link ChatViewMode.SEARCH_RESULT}
     * OR {@link ChatViewMode.MESSAGES} if term is empty.
     *
     * We are assuming that we are looking for the same term in all meetings.
     *
     * @param meetingId
     * @param searchTerm
     */
    public dispatchSearchMessages(meetingId: number, searchTerm: string) {
        const conversationStore = chatStore.getOrCreateConversation(meetingId);
        this.searchTerm$.onNext(searchTerm);

        if (searchTerm) {
            // switch to correct view, so we don't allow sending messages while in search view
            conversationStore.setIsLoading(true);
            this._fetchSearchMessages(meetingId, searchTerm).then((data) => {
                this.searchResult$.onNext(data.messages);
                this.viewMode$.onNext(ChatViewMode.SEARCH_RESULT);
            });
            // we should not change loading state, since we still will navigate to the first result anyway.
            // finally(() => conversationStore.setIsLoading(false));
        } else {
            // if we have no search term, then we can empty the search results.
            this.searchResult$.onNext([]);
            this.viewMode$.onNext(ChatViewMode.SEARCH_RESULT);
        }
    }

    /**
     * Exit Search mode and reset all related state.
     *
     * @param meetingId
     */
    public dispatchExitSearch(meetingId: number) {
        this.onSearchExit$.onNext(meetingId);
        this.searchTerm$.onNext('');
        this.viewMode$.onNext(ChatViewMode.MESSAGES);
        this.searchResult$.onNext([]);
        // fetch regular messages into the view and replace the search results
        this.dispatchFetchMessages(meetingId, 'replace');
    }

    /**
     * "Navigate" to the given message.
     *
     * It will fetch message with it's context and replace the conversation view with the result.
     *
     * @param meetingId
     * @param messageId
     */
    public gotoMessage(meetingId: number, messageId: ChatMessageID) {
        const conversationStore = chatStore.getOrCreateConversation(meetingId);

        conversationStore.setIsLoading(true);

        this.fetchMessageWithContext(messageId)
            .then((data) => {
                if (!data.messages) {
                    return;
                }

                this.conversationsStore.dispatchSetMessages(meetingId, data.messages, {
                    hasPreviousMessages: data.has_prev ?? false,
                    hasNextMessages: data.has_next ?? false,
                });
                this.gotoMessage$.onNext(data.messages.find((el) => el.id == messageId));
                this.viewMode$.onNext(this.searchTerm$.getValue() ? ChatViewMode.SEARCH_RESULT : ChatViewMode.MESSAGES);
            })
            .finally(() => conversationStore.setIsLoading(false));
    }

    /**
     * "Navigate" to latest messages.
     *
     * It will fetch the latest messages and replace the conversation view with the results.
     * @param meetingId
     */
    public gotoLatestMessage(meetingId: number) {
        // we no longer should highlight any message
        this.gotoMessage$.onNext(undefined);
        // this loads latest messages and makes sure we also quit search view
        this.dispatchExitSearch(meetingId);
        // tell the app we are now following latest messages
        this.meetingsStore.dispatchIsStuckToBottom(true);
    }

    /**
     * loads previous messages for conversation
     * @param conversationId
     */
    async fetchPreviousMessages(conversationId: number) {
        const oldestMessageId = await this.conversationsStore.getOldestMessageId$(conversationId).toPromise();

        if (!oldestMessageId) {
            return;
        }

        const { messages, more_pages_exist } = await this.fetchMessages(conversationId, { before: oldestMessageId });

        this.conversationsStore.dispatchMergeMessages(conversationId, messages, more_pages_exist);
    }

    public fetchMessages(
        conversationId: number,
        params: ng.IRequestShortcutConfig['params'] = {}
    ): angular.IPromise<{ messages: ChatConversationMessage[]; more_pages_exist: boolean }> {
        const endpoint = conversationId > 0 ? '/api/v2.0/chat/messages' : '/api/v2.0/chat/messages/public';

        const response = this.$http.get<MessageListResponse>(endpoint, {
            params: {
                meeting: conversationId,
                ...params,
            },
        });

        return response.then((response) => response.data);
    }

    public fetchMessageWithContext(
        messageId: ChatMessageID
    ): angular.IPromise<TradersYardApi.Chat.MessageContextResponse> {
        const response = this.$http.get<TradersYardApi.Chat.MessageContextResponse>(
            `/api/v2.0/chat/messages/${messageId}/context`
        );
        return response.then((response) => response.data);
    }

    /**
     * Search messages from the server and set the result as current messages.
     *
     * @param searchTerm
     * @private
     */
    private _fetchSearchMessages(meetingId: number, searchTerm: string) {
        return this.fetchMessages(meetingId, { content: searchTerm });
    }
}

angular.module('tradersyard').service('ChatConversationService', ChatConversationService);
